import { CardInstance, CardDef } from '../types';
import { getCardById } from '../cards';
import { showCardDetail } from './CardDetailModal';

// 山札操作モーダル
// デッキトップのカードを並べ替えたり、選択したりするUI

type SwapMode = 'reorder' | 'selectTop' | 'selectBottom';

interface SwapModalState {
  cards: CardInstance[];
  mode: SwapMode;
  selectCount: number;
  orderedCards: CardInstance[];
  selectedIndices: Set<number>;
  onComplete: (result: CardInstance[]) => void;
}

let modalState: SwapModalState | null = null;

// mode: 
//   'reorder' - 全カード並べ替え(ジャグラー等)
//   'selectTop' - 選択をデッキ上へ(リポーター等)
//   'selectBottom' - 選択をデッキ下へ(船乗り等)
export function showSwapModal(
  cards: CardInstance[],
  count: number,
  onComplete: (result: CardInstance[]) => void,
  mode?: 'reorder' | 'select' | 'top' | 'bottom'
): void {
  // モード判定
  let swapMode: SwapMode;
  if (mode === 'bottom') {
    swapMode = 'selectBottom';
  } else if (mode === 'top' || mode === 'select' || count < cards.length) {
    swapMode = 'selectTop';
  } else {
    swapMode = 'reorder';
  }
  
  modalState = {
    cards: [...cards],
    mode: swapMode,
    selectCount: (swapMode !== 'reorder') ? count : cards.length,
    orderedCards: [...cards],
    selectedIndices: new Set(),
    onComplete,
  };

  renderSwapModal();
  bindSwapEvents();
}

function renderSwapModal(): void {
  if (!modalState) return;

  const existing = document.getElementById('swapCardModal');
  if (existing) existing.remove();

  const isSelectMode = modalState.mode !== 'reorder';
  const selectedCount = modalState.selectedIndices.size;
  // selectモードは0〜selectCount枚の範囲で選択可能
  const canConfirm = isSelectMode 
    ? selectedCount <= modalState.selectCount 
    : true;

  // モード別の説明文
  let titleText = '';
  let descText = '';
  
  if (modalState.mode === 'reorder') {
    titleText = '🔄 カードを並べ替え';
    descText = 'ドラッグで並べ替え (上から順に引きます)';
  } else if (modalState.mode === 'selectTop') {
    titleText = '⬆️ デッキ上に置くカードを選択';
    descText = `<span style="color: #3498db;">0〜${modalState.selectCount}枚</span>選択 → デッキ上、残り → デッキ下`;
  } else if (modalState.mode === 'selectBottom') {
    titleText = '⬇️ デッキ下に置くカードを選択';
    descText = `<span style="color: #e67e22;">0〜${modalState.selectCount}枚</span>選択 → デッキ下、残り → デッキ上`;
  }

  const modal = document.createElement('div');
  modal.id = 'swapCardModal';
  modal.innerHTML = `
    <div class="swap-overlay" style="
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 3000;
      overflow-y: auto;
      padding: 20px;
    ">
      <div class="swap-content" style="
        background: linear-gradient(145deg, #1e2e30 0%, #152025 100%);
        border: 2px solid ${modalState.mode === 'selectBottom' ? '#e67e22' : '#3498db'};
        border-radius: 16px;
        padding: 24px;
        max-width: 450px;
        width: 90%;
        max-height: 90vh;
        overflow-y: auto;
        box-shadow: 0 0 40px ${modalState.mode === 'selectBottom' ? 'rgba(230, 126, 34, 0.3)' : 'rgba(52, 152, 219, 0.3)'};
      ">
        <h2 style="color: ${modalState.mode === 'selectBottom' ? '#e67e22' : '#3498db'}; margin: 0 0 8px 0; font-size: 1.2rem;">
          ${titleText}
        </h2>
        <p style="color: #aaa; margin: 0 0 16px 0; font-size: 0.85rem;">
          ${descText}
        </p>
        
        ${isSelectMode ? `
          <div style="background: rgba(52, 152, 219, 0.1); border: 1px solid ${modalState.mode === 'selectBottom' ? '#e67e22' : '#3498db'}; border-radius: 8px; padding: 8px 12px; margin-bottom: 12px;">
            <span style="color: ${modalState.mode === 'selectBottom' ? '#e67e22' : '#3498db'};">選択中: ${selectedCount} / ${modalState.selectCount}</span>
          </div>
        ` : ''}
        
        <div id="swapCardList" style="
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 20px;
        ">
          ${modalState.orderedCards.map((card, idx) => renderSwapCard(card, idx)).join('')}
        </div>
        
        <div style="display: flex; gap: 12px; justify-content: flex-end;">
          <button id="btnSwapConfirm" style="
            background: ${canConfirm ? (modalState.mode === 'selectBottom' ? '#e67e22' : '#3498db') : '#555'};
            border: none;
            color: ${canConfirm ? '#fff' : '#888'};
            padding: 10px 20px;
            border-radius: 8px;
            cursor: ${canConfirm ? 'pointer' : 'not-allowed'};
            font-weight: bold;
            font-size: 0.9rem;
          " ${canConfirm ? '' : 'disabled'}>✓ 確定</button>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(modal);
}

function renderSwapCard(card: CardInstance, idx: number): string {
  if (!modalState) return '';
  
  const def = getCardById(card.defId);
  if (!def) return '';

  const catColor = getCategoryColor(def.category);
  const catIcon = getCategoryIcon(def.category);
  const isSelectMode = modalState.mode !== 'reorder';
  const isSelected = modalState.selectedIndices.has(idx);
  const hasAbility = def.abilities.length > 0;

  // 番号表示: 選択モードは選択順、reorderモードは表示順
  let orderNum: number | null = null;
  if (isSelectMode && isSelected) {
    orderNum = Array.from(modalState.selectedIndices).indexOf(idx) + 1;
  } else if (!isSelectMode) {
    orderNum = idx + 1; // reorderモードは表示順 = デッキ順
  }

  // 色分け: selectBottomモードはオレンジ系
  const accentColor = modalState.mode === 'selectBottom' ? '#e67e22' : '#3498db';
  
  // reorderモードはドラッグ可能
  const draggable = !isSelectMode ? 'draggable="true"' : '';

  return `
    <div class="swap-card" data-swap-idx="${idx}" ${draggable} style="
      display: flex;
      align-items: center;
      gap: 12px;
      background: linear-gradient(90deg, ${catColor}22 0%, rgba(0,0,0,0.4) 100%);
      border: 2px solid ${isSelected ? accentColor : catColor};
      border-radius: 8px;
      padding: 12px;
      cursor: pointer;
      transition: all 0.2s;
      ${isSelected ? `box-shadow: 0 0 15px ${accentColor}80;` : ''}
    ">
      <div style="
        background: ${isSelected ? accentColor : 'rgba(255,255,255,0.1)'};
        color: ${isSelected ? '#fff' : '#888'};
        width: 28px;
        height: 28px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 0.9rem;
      ">${orderNum ?? '−'}</div>
      <div style="font-size: 1.3rem;">${catIcon}</div>
      <div style="flex: 1;">
        <div style="color: #fff; font-weight: bold; font-size: 0.9rem;">
          ${def.name}
          ${hasAbility ? '<span style="color: #9b59b6; font-size: 0.7rem; margin-left: 4px;">✨</span>' : ''}
        </div>
        <div style="color: #888; font-size: 0.7rem;">${def.tier}ティア | ${def.category}</div>
      </div>
      <div style="
        background: linear-gradient(135deg, #ffd700 0%, #ff8c00 100%);
        color: #000;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 0.95rem;
      ">⚡${card.currentPower}</div>
      <button class="swap-card-detail" data-idx="${idx}" onclick="event.stopPropagation();" style="
        background: rgba(155, 89, 182, 0.3);
        border: 2px solid #9b59b6;
        color: #9b59b6;
        padding: 8px 12px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 0.8rem;
        font-weight: bold;
      ">詳細</button>
    </div>
  `;
}

function bindSwapEvents(): void {
  if (!modalState) return;
  
  const isSelectMode = modalState.mode !== 'reorder';
  const cards = document.querySelectorAll('.swap-card');
  
  if (isSelectMode) {
    // 選択モード: クリックで選択/解除
    cards.forEach((card) => {
      card.addEventListener('click', handleSelectClick);
    });
  } else {
    // 並べ替えモード: ドラッグ&ドロップで順番入れ替え
    cards.forEach((card) => {
      const htmlCard = card as HTMLElement;
      htmlCard.addEventListener('dragstart', handleDragStart as EventListener);
      htmlCard.addEventListener('dragover', handleDragOver as EventListener);
      htmlCard.addEventListener('drop', handleDrop as EventListener);
      htmlCard.addEventListener('dragend', handleDragEnd as EventListener);
    });
  }

  // 詳細ボタン
  document.querySelectorAll('.swap-card-detail').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      const idx = parseInt((e.currentTarget as HTMLElement).getAttribute('data-idx') ?? '0', 10);
      const card = modalState?.orderedCards[idx];
      if (card) showCardDetail(card);
    }, { capture: true });
  });

  document.getElementById('btnSwapConfirm')?.addEventListener('click', handleConfirm);
}

let draggedIdx: number | null = null;

function handleSelectClick(e: Event): void {
  if (!modalState) return;
  
  const el = e.currentTarget as HTMLElement;
  const idx = parseInt(el.getAttribute('data-swap-idx') ?? '0', 10);

  if (modalState.selectedIndices.has(idx)) {
    modalState.selectedIndices.delete(idx);
  } else if (modalState.selectedIndices.size < modalState.selectCount) {
    modalState.selectedIndices.add(idx);
  }
  
  renderSwapModal();
  bindSwapEvents();
}

// 並べ替えモード: タップした順に並べる
function handleReorderClick(e: Event): void {
  if (!modalState) return;
  
  const el = e.currentTarget as HTMLElement;
  const idx = parseInt(el.getAttribute('data-swap-idx') ?? '0', 10);
  
  if (modalState.selectedIndices.has(idx)) {
    // 選択解除: 選択順をリセット
    modalState.selectedIndices.delete(idx);
  } else {
    // 選択追加
    modalState.selectedIndices.add(idx);
  }
  
  // 選択順にorderedCardsを更新
  const selectedOrder: CardInstance[] = [];
  const remaining: CardInstance[] = [];
  
  for (let i = 0; i < modalState.cards.length; i++) {
    if (modalState.selectedIndices.has(i)) {
      selectedOrder.push(modalState.cards[i]);
    } else {
      remaining.push(modalState.cards[i]);
    }
  }
  
  // 選択順を保持するため、Setをiterateした順で追加
  const orderedSelected: CardInstance[] = [];
  modalState.selectedIndices.forEach((i) => {
    orderedSelected.push(modalState!.cards[i]);
  });
  
  modalState.orderedCards = [...orderedSelected, ...remaining];
  
  renderSwapModal();
  bindSwapEvents();
}

function handleDragStart(e: DragEvent): void {
  const el = e.currentTarget as HTMLElement;
  draggedIdx = parseInt(el.getAttribute('data-swap-idx') ?? '0', 10);
  el.style.opacity = '0.5';
}

function handleDragOver(e: DragEvent): void {
  e.preventDefault();
}

function handleDrop(e: DragEvent): void {
  e.preventDefault();
  if (!modalState || draggedIdx === null) return;
  
  const el = e.currentTarget as HTMLElement;
  const dropIdx = parseInt(el.getAttribute('data-swap-idx') ?? '0', 10);
  
  if (draggedIdx !== dropIdx) {
    const cards = modalState.orderedCards;
    const [removed] = cards.splice(draggedIdx, 1);
    cards.splice(dropIdx, 0, removed);
    
    renderSwapModal();
    bindSwapEvents();
  }
}

function handleDragEnd(e: DragEvent): void {
  const el = e.currentTarget as HTMLElement;
  el.style.opacity = '1';
  draggedIdx = null;
}

function handleShuffle(): void {
  if (!modalState) return;
  
  const cards = modalState.orderedCards;
  for (let i = cards.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cards[i], cards[j]] = [cards[j], cards[i]];
  }
  
  renderSwapModal();
  bindSwapEvents();
}

function handleConfirm(): void {
  if (!modalState) return;
  
  let result: CardInstance[];
  
  if (modalState.mode === 'reorder') {
    // 並べ替えモード: 現在の表示順で返す(上から順にデッキトップ)
    result = modalState.orderedCards;
  } else {
    // 選択モード(selectTop/selectBottom): 選択されたカードのみ返す
    result = Array.from(modalState.selectedIndices).map(idx => modalState!.cards[idx]);
  }

  const callback = modalState.onComplete;
  
  // モーダルを閉じる
  const modal = document.getElementById('swapCardModal');
  if (modal) modal.remove();
  
  modalState = null;

  callback(result);
}

// ヘルパー関数
function getCategoryIcon(category: string): string {
  const icons: Record<string, string> = {
    castle: '🏰', city: '🏙️', space: '🚀', movie: '🎬',
    ship: '🚢', ghost: '👻', park: '🎡',
    warrior: '⚔️', thief: '🗡️', mage: '🔮', machine: '⚙️', undead: '💀',
    dragon: '🐉', spirit: '✨', beast: '🐺', angel: '👼', demon: '😈', golem: '🗿',
  };
  return icons[category] ?? '❓';
}

function getCategoryColor(category: string): string {
  const colors: Record<string, string> = {
    castle: '#8b4513', city: '#4682b4', space: '#483d8b', movie: '#dc143c',
    ship: '20b2aa', ghost: '#4b0082', park: '#ff69b4',
    warrior: '#e74c3c', thief: '#9b59b6', mage: '#3498db', machine: '#95a5a6',
    undead: '#1abc9c', dragon: '#e67e22', spirit: '#00bcd4', beast: '#8d6e63',
    angel: '#ffd700', demon: '#8e44ad', golem: '#607d8b',
  };
  return colors[category] ?? '#666';
}
